import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  signUp: (email: string, password: string) => Promise<{ error: Error | null, data?: { user: User | null; session: Session | null } }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// BYPASS AUTHENTICATION FOR DEVELOPMENT
const USE_MOCK_AUTH = false;

const isWidgetContext = typeof window !== 'undefined' && (
  window.self !== window.top || 
  window.location.pathname.startsWith('/widget') || 
  window.location.hash.startsWith('#/widget')
);

const setLoginCookie = (loggedIn: boolean) => {
  if (typeof window === 'undefined') return;
  const hostname = window.location.hostname;
  const isProd = hostname.endsWith('chatbaze.id');
  const domain = isProd ? 'domain=.chatbaze.id;' : '';
  const secure = isProd ? 'Secure;' : '';
  
  if (loggedIn) {
    document.cookie = `chatbaze_logged_in=true; path=/; ${domain} ${secure} max-age=31536000; SameSite=Lax`;
  } else {
    document.cookie = `chatbaze_logged_in=; path=/; ${domain} ${secure} expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  useEffect(() => {
    let mounted = true;

    if (USE_MOCK_AUTH) {
        // Mock User
        const mockUser = {
            id: '00000000-0000-0000-0000-000000000000',
            email: 'dev@example.com',
            app_metadata: {},
            user_metadata: {},
            aud: 'authenticated',
            created_at: new Date().toISOString()
        } as unknown as User;

        const mockSession = {
            access_token: 'mock-token',
            refresh_token: 'mock-refresh-token',
            expires_in: 3600,
            token_type: 'bearer',
            user: mockUser
        } as unknown as Session;

        setUser(mockUser);
        setSession(mockSession);
        setLoading(false);
        setIsAdmin(true); // Auto admin for dev
        return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: AuthChangeEvent, currentSession: Session | null) => {
        if (!mounted) return;

        console.log(`[Auth] Event: ${event}`, { 
          hasSession: !!currentSession, 
          userId: currentSession?.user?.id,
          email: currentSession?.user?.email 
        });

        // Check login cookie presence
        const hasCookie = typeof document !== 'undefined' && 
          document.cookie.split(';').some(item => item.trim().startsWith('chatbaze_logged_in='));

        // If demo just logged in, skip stale-session check and clear the flag
        const isDemoFreshLogin = typeof sessionStorage !== 'undefined' &&
          sessionStorage.getItem('demo_just_logged_in') === 'true';
        if (isDemoFreshLogin) {
          sessionStorage.removeItem('demo_just_logged_in');
        }

        // Handle stale session check (skip if demo fresh login flag was set)
        if (currentSession && !hasCookie && !isDemoFreshLogin && (event === 'INITIAL_SESSION' || event === 'USER_UPDATED')) {
          console.log('[Auth] Stale session detected via event: no login cookie. Signing out...');
          setSession(null);
          setUser(null);
          setIsAdmin(false);
          setLoading(false);
          supabase.auth.signOut();
          return;
        }

        // Token refreshed successfully
        if (event === 'TOKEN_REFRESHED') {
          console.log('[Auth] Token refreshed');
        }

        if (event === 'SIGNED_OUT') {
          console.log('[Auth] User signed out successfully.');
          setLoginCookie(false);
          
          const isExplicit = sessionStorage.getItem('explicit_signout') === 'true';
          const wasLoggedIn = localStorage.getItem('was_logged_in') === 'true';
          const logoutReason = sessionStorage.getItem('logout_reason');

          setSession(null);
          setUser(null);
          setIsAdmin(false);
          setLoading(false);

          if (logoutReason === 'timeout') {
            sessionStorage.removeItem('logout_reason');
            sessionStorage.removeItem('explicit_signout');
            if (!isWidgetContext) {
              window.location.href = '/login?reason=timeout';
            }
            return;
          }

          if (!isExplicit && wasLoggedIn && !isWidgetContext) {
            window.location.href = '/login';
          }
          
          localStorage.removeItem('was_logged_in');
          sessionStorage.removeItem('explicit_signout');
          
          return;
        }
        
        if (event === 'SIGNED_IN' || event === 'USER_UPDATED' || event === 'INITIAL_SESSION') {
          if (currentSession) {
            setLoginCookie(true);
            localStorage.setItem('was_logged_in', 'true');
            setSession(currentSession);
            setUser(currentSession.user);
            
            setLoading(false);
            
            (async () => {
              try {
                console.log('[Auth] Fetching admin flag from backend for:', currentSession.user.id);
                const API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/$/, '');
                const res = await fetch(`${API_URL}/api/auth/flags`, {
                  headers: {
                    Authorization: `Bearer ${currentSession.access_token}`,
                    Accept: 'application/json',
                  },
                });
                if (!res.ok) {
                  console.error('[Auth] Backend auth flags error status:', res.status);
                }
                let isUserAdmin = false;
                try {
                  const json = await res.json() as { isAdmin?: boolean };
                  if (typeof json.isAdmin === 'boolean') {
                    isUserAdmin = json.isAdmin;
                  }
                } catch (e) {
                  console.error('[Auth] Failed to parse auth flags response:', e);
                }
                if (mounted) {
                  setIsAdmin(isUserAdmin);
                  console.log(`[Auth] Admin check complete for ${currentSession.user.email}: ${isUserAdmin}`);
                }
              } catch (err) {
                console.error('[Auth] Unexpected error during role check:', err);
              }
            })();
          } else {
            // If session is null but event is not initial, it might be a silent sign out
            const wasLoggedIn = localStorage.getItem('was_logged_in') === 'true';
            
            if (event !== 'INITIAL_SESSION' && wasLoggedIn && !isWidgetContext) {
                window.location.href = '/login';
            }
            
            setSession(null);
            setUser(null);
            setIsAdmin(false);
            setLoading(false);
            localStorage.removeItem('was_logged_in');
          }
        }
        
        // Final safety net for any other events that might have been missed
        if (mounted) {
          setLoading(false);
        }
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (mounted) {
        console.log('[Auth] Initial session check:', !!initialSession);
        
        const hasCookie = typeof document !== 'undefined' && 
          document.cookie.split(';').some(item => item.trim().startsWith('chatbaze_logged_in='));

        // Skip stale-session check if demo just logged in
        const isDemoFreshLogin = typeof sessionStorage !== 'undefined' &&
          sessionStorage.getItem('demo_just_logged_in') === 'true';
        if (isDemoFreshLogin) {
          sessionStorage.removeItem('demo_just_logged_in');
        }

        if (initialSession && !hasCookie && !isDemoFreshLogin) {
          console.log('[Auth] Stale session detected in getSession. Clearing...');
          supabase.auth.signOut().then(() => {
            if (mounted) {
              setSession(null);
              setUser(null);
              setLoading(false);
            }
          });
          return;
        }

        if (initialSession) {
          localStorage.setItem('was_logged_in', 'true');
        } else {
          // Verify on initial load
          // We shouldn't trigger popup here, since user is just landing on the site, 
          // but we can clean up stale flags just in case.
          localStorage.removeItem('was_logged_in');
        }
        
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        setLoading(false);
      }
    }).catch(err => {
      console.error('[Auth] Error getting initial session:', err);
      if (mounted) setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (session && !loading) {
      const pendingLink = localStorage.getItem('pending_owner_link');
      if (pendingLink) {
        console.log('[Auth] Found pending owner link, linking user...');
        const API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/$/, '');
        fetch(`${API_URL}/api/auth/link-client`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ owner_id: pendingLink })
        }).then(res => {
          if (res.ok) {
            console.log('[Auth] Successfully linked user to owner');
            localStorage.removeItem('pending_owner_link');
          } else {
            console.error('[Auth] Failed to link user to owner status:', res.status);
          }
        }).catch(err => {
          console.error('[Auth] Error linking user to owner:', err);
        });
      }

      const pendingReferral = localStorage.getItem('pending_referral_code');
      if (pendingReferral) {
        console.log('[Auth] Found pending referral code, linking user...');
        const API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/$/, '');
        fetch(`${API_URL}/api/auth/link-referral`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ referral_code: pendingReferral })
        }).then(res => {
          if (res.ok) {
            console.log('[Auth] Successfully linked user to referral');
            localStorage.removeItem('pending_referral_code');
          } else {
            console.error('[Auth] Failed to link user to referral status:', res.status);
          }
        }).catch(err => {
          console.error('[Auth] Error linking user to referral:', err);
        });
      }
    }
  }, [session, loading]);

  // 15 Minutes Inactivity Timeout
  useEffect(() => {
    if (!session) return;

    const TIMEOUT_MS = 15 * 60 * 1000; // 15 Minutes
    const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

    const updateActivity = () => {
      try {
        localStorage.setItem('last_activity_timestamp', Date.now().toString());
      } catch (err) { /* ignore */ }
    };

    // Initialize activity timestamp if not set
    if (!localStorage.getItem('last_activity_timestamp')) {
      updateActivity();
    }

    // Bind event listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Check inactivity state every 5 seconds
    const interval = setInterval(async () => {
      const lastActivity = localStorage.getItem('last_activity_timestamp');
      if (lastActivity) {
        const elapsed = Date.now() - parseInt(lastActivity, 10);
        if (elapsed >= TIMEOUT_MS) {
          console.warn('[Auth] Inactivity timeout reached. Logging out...');
          localStorage.removeItem('last_activity_timestamp');
          
          // Clear login flags and session
          localStorage.removeItem('was_logged_in');
          
          // Set a temporary session flag for timeout
          sessionStorage.setItem('logout_reason', 'timeout');
          
          try {
            // Standard sign out from Supabase
            await signOut();
          } catch (e) {
            console.error('Signout error during timeout:', e);
            // Fallback redirect if signOut fails
            if (!isWidgetContext) {
              window.location.href = `${window.location.origin}/login?reason=timeout`;
            }
          }
        }
      }
    }, 5000);

    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(interval);
    };
  }, [session]);

  const signUp = async (email: string, password: string) => {
    if (USE_MOCK_AUTH) return { error: null }; // Mock success
    
    console.log("Supabase SignUp attempt for:", email);
    const response = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
      },
    });
    
    console.log("Supabase SignUp Response:", response);
    
    // Check if user is actually created but maybe identity is missing (Duplicate Email Case)
    if (response.data?.user && !response.data.user.identities?.length) {
        console.warn("User created but no identities - likely duplicate email");
        return { 
            error: new Error("This email is already registered. Please sign in instead."), 
            data: response.data 
        };
    }

    return { error: response.error, data: response.data };
  };

  const signIn = async (email: string, password: string) => {
    if (USE_MOCK_AUTH) return { error: null }; // Mock success
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signInWithGoogle = async () => {
    if (USE_MOCK_AUTH) return { error: null };
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });
    return { error: error ?? null };
  };

  const signOut = async () => {
    if (USE_MOCK_AUTH) {
        window.location.reload(); // Simple reload to reset state (or just do nothing)
        return;
    }
    sessionStorage.setItem('explicit_signout', 'true');
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, signUp, signIn, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
